import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent, useCollectionBinding, useContentToken } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

interface Amenity {
  title?: string | null;
  description?: string | null;
  group?: string | null;
  amenity_icon?: string | null;
  amenity_image?: string | null;
  amenity_image_alt_text?: string | null;
  is_featured?: string | null;
  [key: string]: unknown;
}

interface GalleryPhoto {
  image?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
  [key: string]: unknown;
}

// ============================================================================
// Photo slider
// ============================================================================

const SLIDE_INTERVAL = 5000;

interface PhotoSliderProps {
  photos: GalleryPhoto[];
  heading: string;
}

function PhotoSlider({ photos, heading }: PhotoSliderProps) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const count = photos.length;

  const advance = useCallback((dir: 1 | -1) => {
    setCurrent(prev => (prev + dir + count) % count);
  }, [count]);

  useEffect(() => {
    if (count <= 1 || paused) return;
    timerRef.current = setInterval(() => advance(1), SLIDE_INTERVAL);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [count, paused, advance]);

  if (count === 0) return null;

  return (
    <div
      className="amenities-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {photos.map((photo, i) => (
        <VibesitesImage
          key={photo.image || i}
          url={photo.image!}
          alt={photo.alt_text || photo.title || heading}
          className={`amenities-slider-img${i === current ? ' amenities-slider-img--active' : ''}`}
          sizes="(min-width: 768px) 50vw, 100vw"
          priority
        />
      ))}

      {count > 1 && (
        <>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--prev"
            onClick={() => advance(-1)}
            aria-label="Previous photo"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--next"
            onClick={() => advance(1)}
            aria-label="Next photo"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Column component
//
// Each group renders as a self-contained card: image on top, heading, then a
// single-column bullet list. Cards sit side-by-side in the parent grid.
// ============================================================================

interface AmenityColumnProps {
  headingNode: ReactNode;
  headingText: string;
  items: Amenity[];
  photos: GalleryPhoto[];
}

function AmenityColumn({ headingNode, headingText, items, photos }: AmenityColumnProps) {
  const colRef = useScrollReveal<HTMLDivElement>();
  const hasImage = photos.length > 0;

  const listRef = useRef<HTMLUListElement>(null);
  const [scroll, setScroll] = useState({ up: false, down: false });

  const updateScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const up = el.scrollTop > 2;
    const down = el.scrollTop + el.clientHeight < el.scrollHeight - 2;
    setScroll(prev => (prev.up === up && prev.down === down ? prev : { up, down }));
  }, []);

  useEffect(() => {
    updateScroll();
    const el = listRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(updateScroll);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateScroll, items.length]);

  return (
    <div ref={colRef} className="reveal-fade-up amenities-card">
      {hasImage && (
        <div className="amenities-card-image">
          <PhotoSlider photos={photos} heading={headingText} />
        </div>
      )}
      <div className="amenities-card-content">
        {headingNode}
        <div className="amenities-card-list-wrap">
          <ul ref={listRef} className="amenities-card-list" onScroll={updateScroll}>
            {items.map((item, i) => (
              <li key={i} className="amenities-card-item">
                <span className="amenities-card-bullet" aria-hidden="true" />
                <span>{item.title || ''}</span>
              </li>
            ))}
          </ul>
          <div className="amenities-card-fade amenities-card-fade--top" data-visible={scroll.up} aria-hidden="true" />
          <div className="amenities-card-fade amenities-card-fade--bottom" data-visible={scroll.down} aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

// Strip HTML tags from a richtext value so it can be used as a plain
// string (e.g. as fallback alt text on a photo slider).
function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

export function AmenitiesList() {
  const communityHeadingRaw = useContentToken('amenitieslist.communityHeading');
  const apartmentHeadingRaw = useContentToken('amenitieslist.apartmentHeading');
  const communityHeadingText = stripHtml(communityHeadingRaw) || 'Community Amenities';
  const apartmentHeadingText = stripHtml(apartmentHeadingRaw) || 'Interior Luxuries';

  const communityItems = useCollectionBinding<Amenity>('amenitieslist.communityItems', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });

  const apartmentItems = useCollectionBinding<Amenity>('amenitieslist.apartmentItems', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });

  const communityPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.communityPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Community' },
    defaultLimit: 8,
  });

  const apartmentPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.apartmentPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
    defaultLimit: 8,
  });

  useRevealStyles();

  const hasContent = communityItems.length > 0 || apartmentItems.length > 0;

  return (
    <section className="py-8 md:py-12 bg-[var(--vibe-color-background)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {hasContent ? (
          <div className="amenities-stack">
            {communityItems.length > 0 && (
              <AmenityColumn
                headingNode={
                  <RichTextContent
                    path="amenitieslist.communityHeading"
                    label="Community Heading"
                    defaultValue="<p>Community Amenities</p>"
                    as="h3"
                    className="amenities-col-heading"
                  />
                }
                headingText={communityHeadingText}
                items={communityItems}
                photos={communityPhotos}
              />
            )}
            {apartmentItems.length > 0 && (
              <AmenityColumn
                headingNode={
                  <RichTextContent
                    path="amenitieslist.apartmentHeading"
                    label="Apartment Heading"
                    defaultValue="<p>Interior Luxuries</p>"
                    as="h3"
                    className="amenities-col-heading"
                  />
                }
                headingText={apartmentHeadingText}
                items={apartmentItems}
                photos={apartmentPhotos}
              />
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
            <p className="font-body text-lg">Amenities will be displayed here.</p>
          </div>
        )}
      </div>

      <style>{`
        /* Side-by-side cards */
        .amenities-stack {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
          align-items: stretch;
        }

        /* Each card: image on top, heading + list below. Cards stretch to the
           tallest sibling so both columns share the same height. */
        .amenities-card {
          display: flex;
          flex-direction: column;
          background: white;
          border: 1px solid rgba(0, 0, 0, 0.12);
          overflow: hidden;
          height: 100%;
        }

        /* Image panel sits at the top of the card */
        .amenities-card-image {
          position: relative;
          overflow: hidden;
          height: 280px;
          flex-shrink: 0;
        }
        .amenities-card-image .amenities-slider {
          height: 100%;
        }

        .amenities-card-content {
          display: flex;
          flex-direction: column;
          padding: 0 0 1rem;
          flex: 1;
          min-height: 0;
        }

        /* Mobile: stack the two cards. */
        @media (max-width: 768px) {
          .amenities-stack {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 600px) {
          .amenities-card-image {
            height: 220px;
          }
        }

        /* Photo slider */
        .amenities-slider {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
          flex-shrink: 0;
        }
        .amenities-slider-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .amenities-slider-img--active {
          opacity: 1;
        }

        .amenities-slider-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: none;
          background: rgba(0, 0, 0, 0.35);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.3s, background 0.3s;
          z-index: 2;
        }
        .amenities-slider:hover .amenities-slider-arrow {
          opacity: 1;
        }
        .amenities-slider-arrow:hover {
          background: rgba(0, 0, 0, 0.55);
        }
        .amenities-slider-arrow--prev { left: 10px; }
        .amenities-slider-arrow--next { right: 10px; }

        @media (prefers-reduced-motion: reduce) {
          .amenities-slider-img {
            transition: none;
          }
        }

        .amenities-col-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1.75rem;
          font-weight: 700;
          color: var(--vibe-color-text);
          text-align: center;
          padding: 1.5rem 2rem 0.5rem;
          margin: 0;
          letter-spacing: 0.02em;
        }

        @media (max-width: 900px) {
          .amenities-col-heading {
            font-size: 1.4rem;
            padding: 1.25rem 1.25rem 0.5rem;
          }
        }
        @media (max-width: 600px) {
          .amenities-col-heading {
            font-size: 1.25rem;
            padding: 1rem 1rem 0.5rem;
            letter-spacing: 0.01em;
          }
        }

        /* List wrapper provides the positioning context for the scroll fades. */
        .amenities-card-list-wrap {
          position: relative;
          flex: 1;
          min-height: 0;
          max-height: 320px;
          display: flex;
          flex-direction: column;
        }

        /* Single-column bullet list. Capped height keeps both cards even —
           a longer list scrolls within the box rather than growing it. */
        .amenities-card-list {
          list-style: none;
          padding: 0.5rem 2rem 0.5rem;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: var(--vibe-color-primary) transparent;
        }
        .amenities-card-list::-webkit-scrollbar {
          width: 8px;
        }
        .amenities-card-list::-webkit-scrollbar-thumb {
          background: var(--vibe-color-primary);
          border-radius: 4px;
          border: 2px solid white;
        }
        .amenities-card-list::-webkit-scrollbar-track {
          background: transparent;
        }
        @media (max-width: 900px) {
          .amenities-card-list {
            padding: 0.5rem 1.25rem 0.5rem;
          }
        }

        /* Fade hints: fade in only when there is more list to scroll to. */
        .amenities-card-fade {
          position: absolute;
          left: 0;
          right: 0;
          height: 2.25rem;
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .amenities-card-fade[data-visible='true'] {
          opacity: 1;
        }
        .amenities-card-fade--top {
          top: 0;
          background: linear-gradient(to bottom, white, rgba(255, 255, 255, 0));
        }
        .amenities-card-fade--bottom {
          bottom: 0;
          background: linear-gradient(to top, white, rgba(255, 255, 255, 0));
        }

        .amenities-card-item {
          display: flex;
          align-items: flex-start;
          gap: 0.65rem;
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text);
          line-height: 1.5;
        }
        @media (max-width: 600px) {
          .amenities-card-item {
            font-size: 0.9rem;
          }
        }

        .amenities-card-bullet {
          flex-shrink: 0;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--vibe-color-primary);
          margin-top: 0.55em;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities List',
  tokens: {
    'amenitieslist.communityHeading': {
      label: 'Community Heading',
      type: 'richtext',
      defaultValue: '<p>Community Amenities</p>',
    },
    'amenitieslist.apartmentHeading': {
      label: 'Apartment Heading',
      type: 'richtext',
      defaultValue: '<p>Interior Luxuries</p>',
    },
  },
  collectionBindings: {
    'amenitieslist.communityItems': {
      label: 'Community Amenities',
      collection: 'amenities',
    },
    'amenitieslist.apartmentItems': {
      label: 'Apartment Features',
      collection: 'amenities',
    },
    'amenitieslist.communityPhotos': {
      label: 'Community Photos',
      collection: 'photo_gallery',
    },
    'amenitieslist.apartmentPhotos': {
      label: 'Apartment Photos',
      collection: 'photo_gallery',
    },
  },
};

(AmenitiesList as any).contentSchema = contentSchema;

export default AmenitiesList;
